import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Select,
  DatePicker,
  Tag,
  Typography,
  Row,
  Col,
  Tooltip,
  message
} from 'antd';
import {
  ExportOutlined,
  EditOutlined,
  FilterOutlined
} from '@ant-design/icons';
import type { TableColumnsType, TableProps } from 'antd';
import {
  type EvaluationResult
} from '../utils/importTemplate';
import {
  exportInspectionReport,
  type ExportData
} from '../utils/exportUtils';
import { getInspectionBatches, getBatchReport, generateBatchReport } from '../services/api';
import { CITY_LEVELS } from '../utils/constants';
import StatusIndicator from './StatusIndicator';
import EnhancedEditModal from './EnhancedEditModal';
import BatchManager from './BatchManager';
import { StatusCalculationEngine } from '../services/StatusCalculationEngine';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// 解析参考范围的工具函数
const parseReferenceRange = (referenceRange: string): { min?: number; max?: number } => {
  if (!referenceRange || referenceRange === '-') {
    return {};
  }

  // 处理各种格式的参考范围
  // 格式1: "100-200" 或 "100 - 200"
  const rangeMatch = referenceRange.match(/(\d+(?:\.\d+)?)\s*[-~～]\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2])
    };
  }

  // 格式2: "≤100" 或 "<=100"
  const maxOnlyMatch = referenceRange.match(/[≤<=]\s*(\d+(?:\.\d+)?)/);
  if (maxOnlyMatch) {
    return {
      max: parseFloat(maxOnlyMatch[1])
    };
  }

  // 格式3: "≥100" 或 ">=100"
  const minOnlyMatch = referenceRange.match(/[≥>=]\s*(\d+(?:\.\d+)?)/);
  if (minOnlyMatch) {
    return {
      min: parseFloat(minOnlyMatch[1])
    };
  }

  // 格式4: 纯数字，视为最大值
  const numberMatch = referenceRange.match(/^\d+(?:\.\d+)?$/);
  if (numberMatch) {
    return {
      max: parseFloat(referenceRange)
    };
  }

  return {};
};

interface ReportData {
  id: string;
  batchId: string;
  companyName: string;
  businessName: string;
  elementName: string;
  actualValue: number;
  unit: string;
  cityLevel: number;
  responsiblePerson: string;
  checkDate: string;
  referenceRange: string;
  evaluation: EvaluationResult;
  remarks?: string;
  createTime: string;
}

// 旧的 EditModal 已被 EnhancedEditModal 替代

const InspectionReportView: React.FC = () => {
  console.log('🎯 InspectionReportView 组件已加载 - 包含自动修复功能');

  const [data, setData] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [selectedRows, setSelectedRows] = useState<ReportData[]>([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState<Set<string>>(new Set());
  const [showBatchManager, setShowBatchManager] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingData, setEditingData] = useState<ReportData | null>(null);
  const [searchParams, setSearchParams] = useState({
    companyName: '',
    businessName: '',
    elementName: '',
    responsiblePerson: '',
    cityLevel: undefined as number | undefined,
    dateRange: null as any,
  });

  // 筛选后的数据
  const filteredData = React.useMemo(() => {
    return data.filter(item => {
      // 公司名称筛选
      if (searchParams.companyName && !item.companyName.toLowerCase().includes(searchParams.companyName.toLowerCase())) {
        return false;
      }
      
      // 业务名称筛选
      if (searchParams.businessName && !item.businessName.toLowerCase().includes(searchParams.businessName.toLowerCase())) {
        return false;
      }
      
      // 要素名称筛选
      if (searchParams.elementName && !item.elementName.toLowerCase().includes(searchParams.elementName.toLowerCase())) {
        return false;
      }
      
      // 负责人筛选
      if (searchParams.responsiblePerson && !item.responsiblePerson.toLowerCase().includes(searchParams.responsiblePerson.toLowerCase())) {
        return false;
      }
      
      // 城市等级筛选
      if (searchParams.cityLevel !== undefined && item.cityLevel !== searchParams.cityLevel) {
        return false;
      }
      
      // 检查时间筛选
      if (searchParams.dateRange && searchParams.dateRange.length === 2) {
        const [startDate, endDate] = searchParams.dateRange;
        
        // 将检查时间字符串转换为日期对象
        let itemDate: Date;
        try {
          // 处理不同的日期格式
          if (item.checkDate.includes('/')) {
            // 格式如 "2025/10/1"
            itemDate = new Date(item.checkDate);
          } else if (item.checkDate.includes('年')) {
            // 格式如 "2025年10月1日"
            const dateStr = item.checkDate.replace(/年|月|日/g, '').replace(/\s+/g, '');
            const parts = dateStr.split(/[年月日]/);
            itemDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
          } else {
            itemDate = new Date(item.checkDate);
          }
          
          // 使用dayjs进行日期比较
          if (itemDate < startDate.toDate() || itemDate > endDate.toDate()) {
            return false;
          }
        } catch (error) {
          console.warn('日期解析失败:', item.checkDate, error);
          // 如果日期解析失败，默认显示该条数据
        }
      }
      
      return true;
    });
  }, [data, searchParams]);

  // 分页状态
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(100);

  // 带行合并信息的数据处理 - 基于全部数据计算
  const processedFilteredData = React.useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    // 先排序确保相同数据相邻
    const sortedData = [...filteredData].sort((a, b) => {
      const companyCompare = a.companyName.localeCompare(b.companyName);
      if (companyCompare !== 0) return companyCompare;

      const businessCompare = a.businessName.localeCompare(b.businessName);
      if (businessCompare !== 0) return businessCompare;

      return a.elementName.localeCompare(b.elementName);
    });

    // 计算每行的rowSpan信息
    const processedData = sortedData.map((item, index) => {
      const result = { ...item } as any;

      // 计算业务所属创大合并
      let companyRowSpan = 0;
      if (index === 0 || sortedData[index - 1].companyName !== item.companyName) {
        let j = index;
        while (j < sortedData.length && sortedData[j].companyName === item.companyName) {
          j++;
        }
        companyRowSpan = j - index;
      }
      result.companyRowSpan = companyRowSpan;

      // 计算培训业务名称合并
      let businessRowSpan = 0;
      if (index === 0 ||
          sortedData[index - 1].companyName !== item.companyName ||
          sortedData[index - 1].businessName !== item.businessName) {
        let j = index;
        while (j < sortedData.length &&
               sortedData[j].companyName === item.companyName &&
               sortedData[j].businessName === item.businessName) {
          j++;
        }
        businessRowSpan = j - index;
      }
      result.businessRowSpan = businessRowSpan;

      return result;
    });

    return processedData;
  }, [filteredData]);

  // 智能分页 - 重新计算当前页面的合并信息
  const paginatedData = React.useMemo(() => {
    if (!processedFilteredData || processedFilteredData.length === 0) return [];

    // 获取当前页面的数据范围
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageData = processedFilteredData.slice(startIndex, endIndex);

    // 重新计算当前页面的rowSpan
    return pageData.map((item, pageIndex) => {
      const globalIndex = startIndex + pageIndex;
      const result = { ...item } as any;

      // 重新计算业务所属创大合并（仅在当前页面内）
      let companyRowSpan = 0;
      if (pageIndex === 0 || pageData[pageIndex - 1].companyName !== item.companyName) {
        // 检查这个合并组是否完全在当前页面内
        let j = pageIndex;
        while (j < pageData.length && pageData[j].companyName === item.companyName) {
          j++;
        }
        // 检查是否跨页
        const globalJ = globalIndex + (j - pageIndex);
        const isCompleteInPage = globalJ >= processedFilteredData.length ||
                                processedFilteredData[globalJ]?.companyName !== item.companyName;

        companyRowSpan = isCompleteInPage ? j - pageIndex : j - pageIndex;
      }
      result.companyRowSpan = companyRowSpan;

      // 重新计算培训业务名称合并（仅在当前页面内）
      let businessRowSpan = 0;
      if (pageIndex === 0 ||
          pageData[pageIndex - 1].companyName !== item.companyName ||
          pageData[pageIndex - 1].businessName !== item.businessName) {
        let j = pageIndex;
        while (j < pageData.length &&
               pageData[j].companyName === item.companyName &&
               pageData[j].businessName === item.businessName) {
          j++;
        }
        // 检查是否跨页
        const globalJ = globalIndex + (j - pageIndex);
        const isCompleteInPage = globalJ >= processedFilteredData.length ||
                                processedFilteredData[globalJ]?.companyName !== item.companyName ||
                                processedFilteredData[globalJ]?.businessName !== item.businessName;

        businessRowSpan = isCompleteInPage ? j - pageIndex : j - pageIndex;
      }
      result.businessRowSpan = businessRowSpan;

      return result;
    });
  }, [processedFilteredData, currentPage, pageSize]);

  // 当筛选条件变化时，重置到第一页
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filteredData.length]);

  // 获取筛选选项
  const filterOptions = React.useMemo(() => {
    return {
      companyNames: Array.from(new Set(data.map(item => item.companyName))).filter(Boolean),
      businessNames: Array.from(new Set(data.map(item => item.businessName))).filter(Boolean),
      elementNames: Array.from(new Set(data.map(item => item.elementName))).filter(Boolean),
      responsiblePersons: Array.from(new Set(data.map(item => item.responsiblePerson))).filter(Boolean),
    };
  }, [data]);

  // 处理批次选择 - 为后续批量删除功能预留
  // const handleBatchSelection = (batchId: string, checked: boolean) => {
  //   const newSelectedBatchIds = new Set(selectedBatchIds);
  //
  //   if (checked) {
  //     newSelectedBatchIds.add(batchId);
  //   } else {
  //     newSelectedBatchIds.delete(batchId);
  //   }
  //
  //   setSelectedBatchIds(newSelectedBatchIds);
  //
  //   // 更新选中的行：包含所有选中批次的所有数据（不仅是当前页）
  //   const allSelectedRows = data.filter(item => newSelectedBatchIds.has(item.batchId));
  //   setSelectedRows(allSelectedRows);
  // };

  // 计算批次选择状态 - 为后续批量删除功能预留
  // const getBatchSelectionState = (batchId: string) => {
  //   return selectedBatchIds.has(batchId);
  // };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 检查并自动修复缺失的报告
  const checkAndAutoFixBatch = async (batchId: string, expectedCount: number) => {
    try {
      console.log(`🔍 检查批次 ${batchId}，预期数量: ${expectedCount}`);
      const batchReport = await getBatchReport(batchId);
      console.log(`📊 批次 ${batchId} 当前报告数量: ${batchReport.reports.length}`);

      // 如果报告数量匹配预期，无需修复
      if (batchReport.reports.length >= expectedCount) {
        console.log(`✅ 批次 ${batchId} 报告完整，无需修复`);
        return batchReport;
      }

      // 如果报告缺失，尝试自动修复
      console.log(`🔧 批次 ${batchId} 报告缺失 (${batchReport.reports.length}/${expectedCount})，开始自动修复...`);
      setAutoFixing(true);

      message.loading({
        content: `检测到批次 ${batchId} 数据异常，正在自动修复...`,
        key: 'autofix',
        duration: 0,
      });

      const result = await generateBatchReport(batchId);

      if (result.success) {
        message.success({
          content: `自动修复成功！批次 ${batchId} 生成了 ${result.reports_created} 条检查报告`,
          key: 'autofix',
        });

        // 重新获取修复后的报告
        return await getBatchReport(batchId);
      } else {
        message.error({
          content: `自动修复失败：${result.error}`,
          key: 'autofix',
        });

        // 返回原始结果
        return batchReport;
      }
    } catch (error) {
      console.error('自动修复失败:', error);
      message.error({
        content: `自动修复失败：${error instanceof Error ? error.message : '未知错误'}`,
        key: 'autofix',
      });

      // 发生错误时，尝试返回原始报告
      try {
        return await getBatchReport(batchId);
      } catch {
        return { reports: [], summary: null, batch_id: batchId };
      }
    } finally {
      setAutoFixing(false);
    }
  };

  const loadData = async () => {
    setLoading(true);

    try {
      // 获取检查批次列表
      const batches = await getInspectionBatches();

      if (batches.length === 0) {
        setData([]);
        return;
      }

      // 获取所有批次的报告数据并合并
      const allReportData: ReportData[] = [];

      for (const batch of batches) {
        try {
          // 使用自动修复功能获取批次报告
          const batchReport = await checkAndAutoFixBatch(batch.batch_id, batch.record_count);

          // 转换当前批次的数据格式
          const batchReportData: ReportData[] = batchReport.reports.map(report => ({
            id: report.id.toString(),
            batchId: report.batch_id,
            companyName: report.company_name,
            businessName: report.business_name,
            elementName: report.element_name,
            actualValue: report.actual_value,
            unit: '元', // 从报告中获取或使用默认值
            cityLevel: report.city_level,
            responsiblePerson: report.responsible_person,
            checkDate: report.check_date ?
              (report.check_date.includes('年') ?
                // 如果是中文格式（如"2025年8月1日"），转换为标准格式
                (() => {
                  const match = report.check_date.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
                  if (match) {
                    const [, year, month, day] = match;
                    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                  }
                  return report.check_date;
                })() :
                new Date(report.check_date).toISOString().split('T')[0]
              ) :
              new Date(report.created_at).toISOString().split('T')[0], // 后备方案：使用创建时间
            referenceRange: report.reference_range,
            evaluation: {
              status: report.warning_status as any,
              referenceRange: report.reference_range,
              evaluation: report.evaluation || '-'
            },
            remarks: '',
            createTime: report.created_at
          }));

          // 将当前批次的数据添加到总数据中
          allReportData.push(...batchReportData);
        } catch (error) {
          console.error(`加载批次 ${batch.batch_id} 数据失败:`, error);
        }
      }

      // 按创建时间降序排序（最新的在前面）
      allReportData.sort((a, b) => new Date(b.createTime).getTime() - new Date(a.createTime).getTime());

      setData(allReportData);
    } catch (error) {
      console.error('加载数据失败:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: ReportData) => {
    setEditingData(record);
    setEditModalVisible(true);
  };

  const handleEditOk = (values: any) => {
    if (editingData) {
      // 检查检查结果是否发生变化
      const newActualValue = values.actualValue || editingData.actualValue;
      const hasActualValueChanged = newActualValue !== editingData.actualValue;

      // 更新数据，包括检查结果
      const updatedData = data.map(item =>
        item.id === editingData.id
          ? {
              ...item,
              actualValue: newActualValue,
              evaluation: {
                ...item.evaluation,
                evaluation: values.evaluation
              },
              remarks: values.remarks
            }
          : item
      );

      setData(updatedData);

      if (hasActualValueChanged) {
        message.success('更新成功，状态已重新计算');
      } else {
        message.success('更新成功');
      }
    }
    setEditModalVisible(false);
    setEditingData(null);
  };

  const handleExport = (exportType: 'selected' | 'filtered' | 'all' = 'filtered') => {
    let dataToExport: ReportData[] = [];
    let exportDescription = '';
    
    switch (exportType) {
      case 'selected':
        if (selectedRows.length === 0) {
          message.warning('请先选择要导出的数据');
          return;
        }
        dataToExport = selectedRows;
        exportDescription = `选中的${selectedRows.length}条`;
        break;
      case 'filtered':
        dataToExport = filteredData;
        exportDescription = `筛选后的${filteredData.length}条`;
        break;
      case 'all':
        dataToExport = data;
        exportDescription = `全部${data.length}条`;
        break;
    }
    
    if (dataToExport.length === 0) {
      message.warning('没有数据可以导出');
      return;
    }
    
    try {
      // 转换数据格式
      const exportData: ExportData[] = dataToExport.map(item => ({
        id: item.id,
        batchId: item.batchId,
        companyName: item.companyName,
        businessName: item.businessName,
        elementName: item.elementName,
        actualValue: item.actualValue,
        unit: item.unit,
        cityLevel: item.cityLevel,
        responsiblePerson: item.responsiblePerson,
        checkDate: item.checkDate,
        referenceRange: item.referenceRange,
        evaluation: {
          status: item.evaluation.status,
          evaluation: item.evaluation.evaluation
        },
        remarks: item.remarks,
        createTime: item.createTime
      }));
      
      exportInspectionReport(exportData, '检查报告数据');
      message.success(`导出${exportDescription}数据成功`);
    } catch (error) {
      message.error('导出失败: ' + (error as Error).message);
    }
  };

  const columns: TableColumnsType<ReportData> = [
    // {
    //   title: '批次号',
    //   dataIndex: 'batchId',
    //   width: 180,
    //   fixed: 'left',
    //   render: (batchId: string) => (
    //     <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    //       <Checkbox
    //         checked={getBatchSelectionState(batchId)}
    //         onChange={(e) => handleBatchSelection(batchId, e.target.checked)}
    //       />
    //       <span style={{ fontSize: '12px', wordBreak: 'break-all' }}>
    //         {batchId}
    //       </span>
    //     </div>
    //   ),
    // },
    {
      title: '业务所属创大',
      dataIndex: 'companyName',
      width: 120,
      fixed: 'left',
      onCell: (record: any) => ({
        rowSpan: record.companyRowSpan,
      }),
      render: (text: string, record: any) => {
        if (record.companyRowSpan > 0) {
          return (
            <div style={{
              fontWeight: 'bold',
              color: '#1890ff',
              textAlign: 'center',
              padding: '8px 4px',
              background: record.companyRowSpan > 1 ? 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)' : 'transparent',
              borderRadius: record.companyRowSpan > 1 ? '4px' : '0',
              border: record.companyRowSpan > 1 ? '1px solid #91d5ff' : 'none',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {text}
            </div>
          );
        }
        return null;
      },
    },
    {
      title: '培训业务名称',
      dataIndex: 'businessName',
      width: 120,
      onCell: (record: any) => ({
        rowSpan: record.businessRowSpan,
      }),
      render: (text: string, record: any) => {
        if (record.businessRowSpan > 0) {
          return (
            <div style={{
              fontWeight: record.businessRowSpan > 1 ? '600' : 'normal',
              color: record.businessRowSpan > 1 ? '#1890ff' : 'inherit',
              textAlign: 'center',
              padding: '6px 4px',
              background: record.businessRowSpan > 1 ? 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)' : 'transparent',
              borderRadius: record.businessRowSpan > 1 ? '4px' : '0',
              border: record.businessRowSpan > 1 ? '1px solid #adc6ff' : 'none',
              fontSize: record.businessRowSpan > 1 ? '13px' : '12px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {text}
            </div>
          );
        }
        return null;
      },
    },
    {
      title: '培训要素',
      dataIndex: 'elementName',
      width: 100,
    },
    {
      title: '本次检查结果',
      dataIndex: 'actualValue',
      width: 120,
      render: (value, record) => `${value} ${record.unit}`,
    },
    {
      title: '检查时间',
      dataIndex: 'checkDate',
      width: 120,
    },
    {
      title: '主体分级',
      dataIndex: 'cityLevel',
      width: 100,
      render: (level) => (
        <Tag color="blue">
          {CITY_LEVELS[level as keyof typeof CITY_LEVELS]}
        </Tag>
      )
    },
    {
      title: '负责人',
      dataIndex: 'responsiblePerson',
      width: 80,
    },
    {
      title: '参考范围',
      dataIndex: 'referenceRange',
      width: 120,
    },
    {
      title: '智能状态',
      dataIndex: 'evaluation',
      width: 140,
      render: (evaluation: EvaluationResult, record: ReportData) => {
        // 使用新的智能状态指示器
        return (
          <StatusIndicator
            actualValue={record.actualValue}
            referenceRange={record.referenceRange}
            elementName={record.elementName}
            cityLevel={record.cityLevel}
            evaluation={evaluation?.evaluation}
            size="medium"
            showAnimation={true}
            showTooltip={true}
          />
        );
      }
    },
    {
      title: '评定',
      dataIndex: 'evaluation',
      width: 200,
      render: (evaluation: EvaluationResult, record) => (
        <div>
          <Text style={{ fontSize: '12px' }}>
            {evaluation?.evaluation || '-'}
          </Text>
          {record.remarks && (
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                备注: {record.remarks}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button 
              type="text" 
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection: TableProps<ReportData>['rowSelection'] = {
    selectedRowKeys: selectedRows.map(row => row.id),
    onChange: (selectedRowKeys, newSelectedRows) => {
      // 手动行选择时，清除批次选择状态，只保留手动选中的行
      setSelectedBatchIds(new Set());
      setSelectedRows(newSelectedRows);
    },
    onSelectAll: (selected, newSelectedRows, changeRows) => {
      // 全选/取消全选当前页时，清除批次选择状态
      setSelectedBatchIds(new Set());
      if (selected) {
        // 选择当前页所有行
        const currentPageRows = filteredData.slice(0, 20); // 假设每页20条，实际应该从分页状态获取
        setSelectedRows([...selectedRows.filter(row => !currentPageRows.find(r => r.id === row.id)), ...currentPageRows]);
      } else {
        // 取消选择当前页所有行
        const currentPageIds = new Set(changeRows.map(row => row.id));
        setSelectedRows(selectedRows.filter(row => !currentPageIds.has(row.id)));
      }
    },
  };

  // 处理批次管理器的批次选择
  const handleBatchesSelected = (batchIds: string[]) => {
    setSelectedBatchIds(new Set(batchIds));
  };

  // 处理批次删除
  const handleDeleteBatches = (batchIds: string[]) => {
    // TODO: 实现批次删除功能
    console.log('删除批次:', batchIds);
    message.info(`将删除 ${batchIds.length} 个批次`);
  };

  // 处理批次刷新
  const handleRefreshBatches = () => {
    loadData();
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 批次管理区域 */}
      {showBatchManager && (
        <div style={{ marginBottom: '24px' }}>
          <BatchManager
            onBatchesSelected={handleBatchesSelected}
            selectedBatchIds={Array.from(selectedBatchIds)}
            showActions={true}
            onDeleteBatches={handleDeleteBatches}
            onRefreshBatches={handleRefreshBatches}
          />
        </div>
      )}

      <Card>
        <div style={{ marginBottom: '16px' }}>

          {/* 页面头部操作区域 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={4} style={{ margin: 0 }}>检查报告管理</Title>
            <Space>
              <Button
                type={showBatchManager ? "primary" : "default"}
                onClick={() => setShowBatchManager(!showBatchManager)}
                icon={<FilterOutlined />}
              >
                {showBatchManager ? '隐藏批次管理' : '显示批次管理'}
              </Button>
            </Space>
          </div>

          {/* 筛选条件区域 */}
          <div style={{ background: '#fafafa', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Title level={5} style={{ margin: 0, color: '#666' }}>筛选条件</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 提示：可以组合使用多个筛选条件，支持模糊搜索
              </Text>
            </div>
            
            {/* 第一行：基本信息筛选 */}
            <Row gutter={12} style={{ marginBottom: '12px' }}>
              <Col span={5}>
                <Select
                  placeholder="业务所属创大"
                  style={{ width: '100%' }}
                  value={searchParams.companyName || undefined}
                  onChange={(value) => setSearchParams(prev => ({
                    ...prev,
                    companyName: value || ''
                  }))}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {filterOptions.companyNames.map(name => (
                    <Select.Option key={name} value={name}>{name}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={5}>
                <Select
                  placeholder="培训业务名称"
                  style={{ width: '100%' }}
                  value={searchParams.businessName || undefined}
                  onChange={(value) => setSearchParams(prev => ({
                    ...prev,
                    businessName: value || ''
                  }))}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {filterOptions.businessNames.map(name => (
                    <Select.Option key={name} value={name}>{name}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="培训要素"
                  style={{ width: '100%' }}
                  value={searchParams.elementName || undefined}
                  onChange={(value) => setSearchParams(prev => ({
                    ...prev,
                    elementName: value || ''
                  }))}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {filterOptions.elementNames.map(name => (
                    <Select.Option key={name} value={name}>{name}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="负责人"
                  style={{ width: '100%' }}
                  value={searchParams.responsiblePerson || undefined}
                  onChange={(value) => setSearchParams(prev => ({
                    ...prev,
                    responsiblePerson: value || ''
                  }))}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {filterOptions.responsiblePersons.map(name => (
                    <Select.Option key={name} value={name}>{name}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={3}>
                <Select
                  placeholder="主体分级"
                  style={{ width: '100%' }}
                  value={searchParams.cityLevel}
                  onChange={(value) => setSearchParams(prev => ({
                    ...prev,
                    cityLevel: value
                  }))}
                  allowClear
                >
                  {Object.entries(CITY_LEVELS).map(([value, label]) => (
                    <Select.Option key={value} value={parseInt(value)}>
                      {label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={3}>
                <Space size="small">
                  <Button
                    size="small"
                    onClick={() => setSearchParams({
                      companyName: '',
                      businessName: '',
                      elementName: '',
                      responsiblePerson: '',
                      cityLevel: undefined,
                      dateRange: null
                    })}
                  >
                    清空
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<FilterOutlined />}
                    loading={loading || autoFixing}
                    onClick={loadData}
                  >
                    {autoFixing ? '自动修复中...' : '刷新'}
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* 第二行：时间筛选 */}
            <Row gutter={12}>
              <Col span={8}>
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['检查开始日期', '检查结束日期']}
                  value={searchParams.dateRange}
                  onChange={(dates) => setSearchParams(prev => ({
                    ...prev,
                    dateRange: dates
                  }))}
                  allowClear
                />
              </Col>
              <Col span={16}>
                <Text type="secondary" style={{ fontSize: '12px', lineHeight: '32px' }}>
                  💡 筛选提示：支持多条件组合筛选，选择条件后数据会自动更新，点击清空可重置所有筛选条件
                </Text>
              </Col>
            </Row>
          </div>
          
          {/* 筛选统计和操作按钮 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <Text type="secondary">
                {filteredData.length !== data.length ? 
                  `筛选显示 ${filteredData.length} 条，共 ${data.length} 条数据` : 
                  `共 ${data.length} 条数据`
                }
              </Text>
              {selectedBatchIds.size > 0 && (
                <div style={{ marginTop: '4px' }}>
                  <Text type="warning" style={{ fontSize: '12px' }}>
                    已选择 {selectedBatchIds.size} 个批次，共 {selectedRows.length} 条记录
                  </Text>
                </div>
              )}
              {selectedRows.length > 0 && selectedBatchIds.size === 0 && (
                <div style={{ marginTop: '4px' }}>
                  <Text style={{ fontSize: '12px', color: '#1890ff' }}>
                    已手动选择 {selectedRows.length} 条记录
                  </Text>
                </div>
              )}
            </div>
            <Space>
              <Button 
                type="primary" 
                icon={<ExportOutlined />}
                onClick={() => handleExport('all')}
              >
                导出全部数据 ({data.length})
              </Button>
              <Button 
                icon={<ExportOutlined />}
                onClick={() => handleExport('filtered')}
                disabled={filteredData.length === 0}
              >
                导出筛选数据 ({filteredData.length})
              </Button>
              <Button 
                icon={<ExportOutlined />}
                onClick={() => handleExport('selected')}
                disabled={selectedRows.length === 0}
              >
                导出选中数据 ({selectedRows.length})
              </Button>
              {(selectedRows.length > 0 || selectedBatchIds.size > 0) && (
                <Button 
                  onClick={() => {
                    setSelectedRows([]);
                    setSelectedBatchIds(new Set());
                  }}
                >
                  清空选择
                </Button>
              )}
            </Space>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={paginatedData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1400, y: 600 }}
          sticky={{ offsetHeader: 64 }}
          bordered
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: processedFilteredData.length,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录（行合并模式）`,
            pageSizeOptions: ['20', '50', '100', '200'],
            position: ['bottomCenter'],
            showLessItems: true,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1); // 改变页面大小时回到第一页
              }
            },
          }}
          size="middle"
          className="inspection-report-table"
          locale={{
            emptyText: (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>暂无检查报告数据</p>
                <p style={{ color: '#999' }}>
                  请先通过"检查报告导入"功能导入数据
                </p>
                <Space>
                  <Button onClick={loadData} loading={loading || autoFixing}>
                    {autoFixing ? '自动修复中...' : '刷新数据'}
                  </Button>
                  <Button type="primary" href="/import-new">
                    立即导入
                  </Button>
                </Space>
              </div>
            )
          }}
        />
      </Card>

      <EnhancedEditModal
        visible={editModalVisible}
        data={editingData}
        onOk={handleEditOk}
        onCancel={() => setEditModalVisible(false)}
      />

      {/* 表格样式 */}
      <style>{`
        .inspection-report-table .ant-table-thead > tr > th {
          background: #f8f9fa !important;
          font-weight: 600;
          text-align: center;
          border-bottom: 2px solid #e9ecef;
        }
        
        .inspection-report-table .ant-table-tbody > tr > td {
          text-align: center;
          vertical-align: middle;
        }
        
        .inspection-report-table .ant-table-tbody > tr:hover > td {
          background: #f0f8ff !important;
        }
        
        .inspection-report-table .ant-table-cell {
          padding: 8px 12px;
        }
        
        .inspection-report-table .ant-table-row-selected > td {
          background: #e6f4ff !important;
        }

        /* 合并单元格样式优化 */
        .inspection-report-table .ant-table-tbody > tr > td[rowspan] {
          border-right: 2px solid #e6f7ff;
          position: relative;
        }
        .inspection-report-table .ant-table-tbody > tr > td[rowspan]::after {
          content: '';
          position: absolute;
          right: -1px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(to bottom, #1890ff, #40a9ff);
          opacity: 0.6;
        }
      `}</style>
    </div>
  );
};

export default InspectionReportView;